<template>
	<VDataGridV2
		:loader="dataLoader"
		entity-type="characteristic"
		draggable-rows
		class="characteristics-registry"
		:context-menu="CharacteristicsService.contextMenu"
		@row:dblclick="openCharacteristicCard"
		@row:drag-start="onRowDragStart"
		@row:drop="onRowDrop"
	>
		<VDataGridColumnV2
			id="id"
			label="№"
		>
			<template #default="{ row }">
				<VLink @click="openCharacteristicCard(row)">{{ row.id }}</VLink>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="name"
			label="Название"
			width="500px"
			filterable
			filter-send-name="pattern"
			:filter-props="{ placeholder: 'Название', clearable: true }"
			content-class="ellipsis"
		>
			<template #default="{ row }">
				{{ row.name }}
				<div
					class="description ellipsis"
				>
					Описание: {{ row.description ? row.description : '--' }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="unit"
			label="Ед. измерения"
			width="125px"
			content-align="center"
			header-class="nowrap"
		>
			<template #default="{ row }">
				<template v-if="row.unit && row.unit.name !== 'строка'">
					{{ row.unit.name }}
				</template>
			</template>
		</VDataGridColumnV2>

		<VDataGridActionBarV2>
			<VButton theme="primary" @click="openCharacteristicCard()">
				Добавить характеристику
			</VButton>
		</VDataGridActionBarV2>
	</VDataGridV2>
</template>

<script lang="ts">
import Characteristic         from '@/domains/Characteristics/Characteristic';
import CharacteristicsService from '@/domains/Characteristics/CharacteristicsService';
import CharacteristicGroup 	  from '@/domains/CharacteristicGroups/CharacteristicGroup';
import DataGridLoader         from '@/lib/components/VDataGridV2/DataGridLoader';

export default defineComponent({
	name: 'CharacteristicsRegistry',

	props: {
		/**
		 * Выбранная группа:
		 *   объект → фильтр по group_id
		 *   null   → "Без группы" (onlyEmpty: true)
		 */
		selectedGroup: {
			type:    Object as () => CharacteristicGroup | undefined,
			default: undefined,
		},
	},

	setup() {
		return { CharacteristicsService }
	},

	data() {
		return {
			/** Строка, которую тащим (для drag-n-drop сортировки) */
			dragTarget: null as Characteristic | null,

			dataLoader: new DataGridLoader(CharacteristicsService.getListPaginated)
				.useReactiveSource(() => this.buildExternalFilters()),
		};
	},

	methods: {
		buildExternalFilters(): Record<string, unknown> {
			const filters: Record<string, unknown> = { isDeleted: false };

			if (this.selectedGroup) {
				filters.groupId = this.selectedGroup.id;
			} else {
				filters.onlyEmpty = true;
			}

			return filters;
		},

		/** Без строки — создание новой характеристики в выбранной группе */
		async openCharacteristicCard(row?: Characteristic): Promise<void> {
			if (await CharacteristicsService.showCharacteristicCard(row, this.selectedGroup) === true) {
				await this.dataLoader.refresh();
			}
		},

		// ==================== Drag-n-drop сортировка ====================

		onRowDragStart({ row }: { row: Characteristic }) {
			this.dragTarget = row;
		},

		async onRowDrop({ row, pasteMode }: { row: Characteristic; pasteMode: 'before' | 'after' }) {
			if (!this.dragTarget || this.dragTarget.id === row.id) {
				this.dragTarget = null;
				return;
			}

			const success = await CharacteristicsService.changeSort(
				this.dragTarget.id,
				row.id,
				pasteMode === 'after',
			);

			this.dragTarget = null;

			if (success === true) {
				this.dataLoader.refresh();
			}
		},
	},
});
</script>